import React, { useContext, useState } from 'react';
import { AccountContext } from '@/context/account.context';
import t from '@/utils/Language/ResourceCard-translations';
import lang from '@/utils/Language';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Image from 'next/image';
import mono_logo from '@/assets/images/brand/mono_logo_only.png';
import SuggestEditPanel from '@/components/Resources/ResourceCard/ResourceCardPanels/SuggestEditPanel/';
import ActionButtons from '@/components/Resources/ResourceCard/ActionButtons';
import RecommendedResourceIndicator from '@/components/Resources/ResourceCard/RecommendedResourceIndicator';
import ResourceTag from '@/components/Resources/ResourceTag/ResourceTag';
import {
  InfoPanel,
  MapPanel,
  ContactPanel,
  OrgNameAndTitlePanel,
  AddressPanel,
  ButtonPanel
} from '@/components/Resources/ResourceCard/ResourceCardPanels';
import ShareResourceDropdown from '@/components/ShareResourceDropdown/ShareResourceDropdown';
import Button from '@/components/Button/Button';
import useMixpanel from '@/hooks/useMixpanel';
import { resourceHelpers, searchHelpers } from '@/utils/helpers';
import Col from 'react-bootstrap/Col';
import classes from './ResourceResult.module.scss';

/**
 * Resource card used by ResultsScreen (React assessments), Results page of allHome (stayhousedbayarea)
 * @param {string} secondOrigin - origin_type to send in Access API along with "Self", e.g. for resources saved from an EHR assessment like PEARLS.
 */
const ResourceResult = ({ panelButtonSettings = {}, resource, project, secondOrigin, shareMenuData = {} }) => {
  const [openPanel, setOpenPanel] = useState();
  const { track } = useMixpanel();
  const account = useContext(AccountContext);

  const suggestEditPanelClassName = {
    tagIcon: classes.tagIcon,
    editSuggestContainer: classes.editSuggestContainer,
    editSuggestLink: classes.editSuggestLink
  };

  const orgNameAndTitlePanelClassNames = {
    resourceIcon: classes.resourceIcon,
    orgName: classes.orgName,
    oppName: classes.oppName,
    nameTitleWrapper: classes.nameTitleWrapper,
    noLink: classes.noLink
  };

  const addressPanelClassNames = {
    address: classes.address
  };

  const panelButtons = [
    Object.assign(
      {
        type: 'secondary',
        shape: 'rounded',
        label: 'Info',
        panel: 'info',
        icon: { style: 'fas', font: 'info-circle', position: 'left', color: 'white' },
        text: t('Info'),
        styles: { width: 'full' },
        onClick: () => setOpenPanel(openPanel === 'info' ? null : 'info')
      },
      panelButtonSettings.info || {}
    ),
    Object.assign(
      {
        type: 'secondary',
        shape: 'rounded',
        label: 'Map',
        panel: 'map',
        icon: { style: 'fas', font: 'location-dot', position: 'left', color: 'white' },
        text: t('Map'),

        styles: { width: 'full' },
        onClick: () => setOpenPanel(openPanel === 'map' ? null : 'map')
      },
      panelButtonSettings.map || {}
    )
  ];

  const shareButtonStyle = Object.assign(
    {
      buttonType: 'secondary',
      buttonShape: 'rounded',
      buttonText: t('Share'),
      buttonBorder: false,
      buttonIcon: { style: 'fas', font: 'share-alt', position: 'left', color: 'white' },
      buttonWidth: 'full',
      columns: { xs: '4' }
    },
    panelButtonSettings.share || {}
  );

  const renderPanelButtons = () => {
    return (
      <div className={classes.panelButtonsRow}>
        <div className={classes.buttonPanelWrapper}>
          <ButtonPanel
            panelButtons={panelButtons}
            openPanel={openPanel}
            additionalClasses={`${classes.buttonClass} ${classes.panelButton}`}
            disabled={false}
          />
        </div>
        <div className={classes.shareWrapper}>
          <ShareResourceDropdown
            resource={resource}
            buttonStyles={shareButtonStyle}
            additionalClasses={`${classes.buttonClass} ${classes.panelButton} ${classes.shareDropdown}`}
            {...shareMenuData}
          />
        </div>
      </div>
    );
  };

  const closePanel = () => {
    setOpenPanel(null);
  };

  const actionButtonVisibility = {
    print: 'false',
    share: 'false',
    action: 'true',
    bookmark: project == 'allhome' && !account?.isPro ? 'false' : 'true',
    addToCollection: project == 'allhome' ? 'false' : 'true',
    signUp: project == 'allhome' && !account?.isLoggedIn ? 'true' : 'false'
  };

  const trackWTDN = (type, value) => {
    track('Viewed resource', { Opportunity: resource.title, Organization: resource.organization.name });
    switch (type) {
      case 'tel':
        document.location.href = 'tel:' + String(value);
        break;
      case 'email':
        document.location.href = 'mailto:' + String(value);
        break;
      case 'link':
        window.open(value, '_blank', 'noreferrer');
        break;
    }
  };

  return (
    <>
      <div className={`resource-result ${classes[project]} ${classes.organizationInfoContainer}`}>
        <div>
          <div className={classes.infoContainer}>
            {project === 'allhome' && (
              <div>
                <RecommendedResourceIndicator resource={resource} />
              </div>
            )}
            <div className={classes.orgNameTitleAddContainer}>
              <div className={classes.topRow}>
                <OrgNameAndTitlePanel
                  resource={resource}
                  additionalClass={orgNameAndTitlePanelClassNames}
                  linkTitle={project !== 'allhome'}
                  linkSubtitle={project !== 'allhome'}
                  displayIcon={true}
                />
                {project === 'onedegree' && (
                  <div className={classes.actionButtonWrapper}>
                    <ActionButtons
                      resource={resource}
                      buttonVisibility={actionButtonVisibility}
                      buttonShape={'pill'}
                      additionalClasses={classes.referrerButton}
                      secondOrigin={secondOrigin}
                    />
                  </div>
                )}
              </div>
              <AddressPanel resource={resource} additionalClass={addressPanelClassNames} />
            </div>
            {project === 'onedegree' && (
              <div>
                <Col xs={12}>
                  {resource?.tags?.map((tag, index) => (
                    <ResourceTag key={index} includeTagIcon={false} linkTo={searchHelpers.searchTagLink(tag)} tag={tag} />
                  ))}
                </Col>
              </div>
            )}

            <p className={classes.resourceDescription}>{resource.description}</p>
            <div className={classes.buttonWrapper}>{renderPanelButtons()}</div>
            {
              {
                info: (
                  <InfoPanel
                    resource={resource}
                    language={lang}
                    project={project}
                    includeSuggestEdits={false}
                    includeTagList={project !== 'onedegree'}
                    includeFilterNote={project !== 'onedegree'}
                    includeDescription={false}
                  />
                ),
                contact: <ContactPanel resource={resource} language={lang} />,
                map: <MapPanel resource={resource} language={lang} project={project} />
              }[openPanel]
            }
            {openPanel && (
              <>
                <Col xs={12} className={classes.cardDivider}></Col>
                <Col xs={12} className={classes.closePanelSection}>
                  <Button type="link" text={t('Close details')} onButtonClick={closePanel} />
                </Col>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={`${classes[project]} ${classes.whatToDoNextInfoContainer}`}>
        <div>
          <div className={classes.infoContainer}>
            <div className={classes.lineWrapper}>
              <span className={classes.line} />
            </div>
            <div className={classes.whatToDoNext}>{t('What to do next')}</div>
            <div className={classes.resourceAccessInstructionsInfo}>
              {resource.access_instructions &&
                resource.access_instructions.map((instruction, index) => {
                  return (
                    <React.Fragment key={index}>
                      <div className={classes.instructionInfoContainer}>
                        <div className={classes.instructionImgContainer}>
                          <h1 className={classes.instructionImg}>
                            {instruction.access_type === 'phone' && <FontAwesomeIcon icon={['fas', 'phone']} />}
                            {instruction.access_type === 'email' && <FontAwesomeIcon icon={['fas', 'message']} />}
                            {instruction.access_type === 'link' && <FontAwesomeIcon icon={['fas', 'link']} />}
                            {instruction.access_type === 'location' && <FontAwesomeIcon icon={['fas', 'male']} />}
                          </h1>
                        </div>
                        <div className={classes.instructionAccessValueContainer}>
                          {instruction.access_type === 'phone' && instruction.access_value && (
                            <>
                              <span className={classes.instructionAccessValue}>
                                <Button
                                  type={'link'}
                                  text={`${t('Call')} ${resourceHelpers.formatPhoneNumber(instruction.access_value)}`}
                                  additionalClasses={`${classes.instructionAccessValue} text-start`}
                                  onButtonClick={() => trackWTDN('tel', instruction.access_value)}
                                />
                              </span>
                              &nbsp;
                            </>
                          )}
                          {instruction.access_type === 'email' && instruction.emails[0]?.email && (
                            <>
                              <span className={classes.instructionAccessValue}>
                                <Button
                                  type={'link'}
                                  text={`${t('Email')} ${instruction.emails[0].email}`}
                                  additionalClasses={`${classes.instructionAccessValue} text-start`}
                                  onButtonClick={() => trackWTDN('email', instruction.emails[0].email)}
                                />
                              </span>
                              &nbsp;
                            </>
                          )}
                          {instruction.access_type === 'link' && instruction.access_value && (
                            <>
                              <span className={classes.instructionAccessValue}>
                                <Button
                                  type={'link'}
                                  text={`${t('Click to visit link')} ${instruction.access_value}`}
                                  additionalClasses={`${classes.instructionAccessValue} text-start`}
                                  onButtonClick={() => trackWTDN('link', instruction.access_value)}
                                />
                              </span>
                              &nbsp;
                            </>
                          )}
                          {instruction.access_type === 'location' && (
                            <>
                              <span className={classes.instructionAccessLabel}>{t('Go in person')}</span>&nbsp;
                            </>
                          )}
                          {instruction.access_type === 'other' && (
                            <>
                              <span className={classes.instructionAccessValue}>{instruction.access_value}</span>&nbsp;
                            </>
                          )}
                          <br />
                          <span className={classes.instructionInfo}>
                            {['phone', 'link', 'location', 'email'].includes(instruction.access_type) &&
                              instruction.instructions &&
                              instruction.instructions}
                          </span>
                          {instruction.access_type === 'location' && instruction.locations && (
                            <ul className="list-unstyled indented">
                              {instruction.locations.map((location, index) => {
                                return (
                                  <li key={index}>
                                    <span className={classes.instructionInfo}>{location.name}</span>
                                    <br />
                                    <span className={classes.instructionInfo}>{location.address}</span>
                                    <br />
                                    <span className={classes.instructionInfo}>
                                      {location.city}, &nbsp;{location.state}&nbsp;{location.zip_code}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
            </div>
            {project !== 'onedegree' && (
              <div className={classes.oneDegreeFeatureBox}>
                <div className={classes.oneDegreeLogo}>
                  <Image alt="One Degree" src={mono_logo} className={classes.monoLogo} />
                  <div className={classes.featureTextWrapper}>{t('One Degree features')}</div>
                </div>
                <div className={classes.actionButtonWrapper}>
                  <ActionButtons
                    resource={resource}
                    buttonVisibility={actionButtonVisibility}
                    additionalClasses={classes.referrerButton}
                    secondOrigin={secondOrigin}
                  />
                </div>
              </div>
            )}
            <div>
              <SuggestEditPanel resource={resource} additionalClass={suggestEditPanelClassName} project={project} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResourceResult;
